import { Function } from "@baseline/common";
import { ArgumentError } from "@baseline/common/errors";
import { DefaultEventEmitter } from "@baseline/runtime/events";
import { Module } from "@baseline/runtime/webpack/module";
import { FC } from "react";
import { ComponentModule } from "../runtime";


export class MutableComponentRegistry extends DefaultEventEmitter<Event>
{
    constructor()
    {
        super();
        this.onModuleLoad = this.onModuleLoad.bind(this);

        Module.on("load", this.onModuleLoad);
    }

    resolve(component: FC): FC
    {
        // Resolve or register context
        let context =
            this.components.get(component) ??
            this.register(component);

        // Return latest
        return context.component;
    }

    components = new Map<FC, ComponentContext>();
    contexts: ComponentContext[] = [];


    private register(component: FC): ComponentContext
    {
        // Resolve defining module and export name
        const module =
            Module.getExportModules(component, true)
                .find(ComponentModule.isComponentModule);
        if (!module)
            throw new ArgumentError(`Can't find implementing module for supplied component: ${component.name}.`);
        const exportName = Module.getExportName(module, component);

        // Enable hot module
        module.hot?.accept();

        // Create context
        const context: ComponentContext = { module, exportName, component };
        this.contexts.push(context);

        // Add to components
        this.components.set(component, context);

        // Return context
        return context;
    }

    private onModuleLoad(module: Module): void
    {
        if (!ComponentModule.isComponentModule(module))
            return;

        // Find impacted contexts
        const contexts =
            this.contexts.filter(context => context.module.id == module.id);
        if (contexts.length > 0)
        {
            // Process each context
            for (var context of contexts)
            {
                // Update module and component
                context.module = module;
                context.component = module.exports[context.exportName];

                // Add to components
                this.components.set(context.component, context);
            }

            // Accept hot module update
            module.hot?.accept();

            // Emit event
            Function.runAsync(() => this.emit("componentUpdate", contexts));
        }
    }
}


export type Event = "componentUpdate";


interface ComponentContext
{
    module: ComponentModule;
    exportName: string;
    component: FC;
}
